import { z } from 'zod';
import {
  COMMISSIONING_CAP_METRICS,
  COMMISSIONING_CAP_TYPES,
  COMMISSIONING_CLUSTER_ENTITY_TYPES,
  COMMISSIONING_CONFIGURATOR_TYPE_CODES,
  COMMISSIONING_EVENT_SOURCE_TYPES,
  COMMISSIONING_INSTANCE_SCOPES,
} from '../types/commissioning';
import { COMMISSIONING_PUBLICATION_STATUSES } from '../types/commissioning-kernel';
import type { CommissioningGateV2, CommissioningTotalBonusMalusConfig } from '../types/commissioning';
import type { CommissioningPreflightFinding } from '../types/commissioning-runtime-v3';
import { commissioningFunctionEffectSchema } from './commissioning-kernel';
import { commissioningThresholdValuesStatusSchema } from './commissioning-declarative-thresholds';
import { commissioningPerimeterSchedaSchema } from './commissioning-instance-perimeter';

const uuid = z.string().uuid();

/**
 * Punto di configurazione a cui rimanda una voce della scheda o un finding del
 * preflight. È il contratto dei deep link: la UI traduce ogni target nel
 * tab/dialog corretto (drawer identità, tab del pannello assegnazioni,
 * template nel catalogo, sezioni della gara, cataloghi L1/L3, archi DAG).
 */
export const COMMISSIONING_SCHEDA_TARGETS = [
  /** Identità del configuratore: driver/sorgenti, VP, periodo, fonte evento, CAP (drawer di modifica). */
  'instance_form',
  /** Pannello assegnazioni → tab Cluster (attivazione, TGT, celle driver). */
  'assignments_clusters',
  /** Pannello assegnazioni → tab Regole (valori Soglie per cluster, regole di periodo). */
  'assignments_rules',
  /** Pannello assegnazioni → tab Funzioni & leve. */
  'assignments_functions',
  /** Template nel catalogo configuratori (struttura, paletti, CAP di default, bonus/malus sul totale). */
  'template',
  /** Anagrafica della gara (famiglia, insegna/canale, periodo, ammissione). */
  'competition',
  /** Pacchetti valenze abbinati alla gara. */
  'competition_value_packages',
  /** Partecipanti e cluster della gara. */
  'participants',
  /** Archi del DAG (interazioni fra configuratori). */
  'interactions',
  /** Catalogo funzioni L3. */
  'catalog_functions',
  /** Catalogo variabili (cross_ref). */
  'catalog_variables',
] as const;
export type CommissioningSchedaTarget = typeof COMMISSIONING_SCHEDA_TARGETS[number];

/** Modalità di lavoro del configuratore rispetto ai driver della gara. */
export const COMMISSIONING_SCHEDA_DRIVER_MODES = ['driver', 'extra_sources', 'extra_period'] as const;
export type CommissioningSchedaDriverMode = typeof COMMISSIONING_SCHEDA_DRIVER_MODES[number];

const preflightFindingKind = z.custom<CommissioningPreflightFinding['kind']>(
  (value) => typeof value === 'string' && value.length > 0,
  'kind del finding non valido',
);

/**
 * Voce della checklist "Cosa manca": un finding del preflight riferito al
 * configuratore, con il punto di configurazione che lo risolve. Nessuna
 * regola propria: la checklist è una proiezione delle stesse regole del
 * preflight (stessa severità, stesso messaggio).
 */
export const commissioningSchedaChecklistItemSchema = z.object({
  kind: preflightFindingKind,
  severity: z.enum(['error', 'warning']),
  message: z.string().min(1),
  target: z.enum(COMMISSIONING_SCHEDA_TARGETS),
  /** Altri configuratori coinvolti nello stesso finding (cicli, archi). */
  relatedInstanceIds: z.array(uuid),
}).strict();
export type CommissioningSchedaChecklistItem = z.infer<typeof commissioningSchedaChecklistItemSchema>;

export const commissioningSchedaChecklistSchema = z.object({
  /** Istante del preflight da cui deriva la checklist. */
  generatedAt: z.string().min(1),
  /** Finding di livello error riferiti al configuratore: bloccano l'attivazione. */
  blockingCount: z.number().int().nonnegative(),
  warningCount: z.number().int().nonnegative(),
  /** Errori della gara non riferiti a un configuratore (roster, scoping, cicli…): bloccano comunque. */
  competitionBlockingCount: z.number().int().nonnegative(),
  items: z.array(commissioningSchedaChecklistItemSchema),
  /**
   * Il preflight della gara non è eseguibile (es. contesto/roster incompleto):
   * motivo leggibile, voci vuote e blocco di gara conteggiato. Mai un report
   * vuoto spacciato per "tutto a posto".
   */
  unavailableReason: z.string().nullable(),
}).strict();
export type CommissioningSchedaChecklist = z.infer<typeof commissioningSchedaChecklistSchema>;

const schedaDriverRefSchema = z.object({
  id: uuid,
  name: z.string(),
  /** false = driver non più ammesso dalla gara (resta visibile, il preflight lo segnala). */
  eligible: z.boolean(),
}).strict();
export type CommissioningSchedaDriverRef = z.infer<typeof schedaDriverRefSchema>;

export const commissioningSchedaIdentitySchema = z.object({
  id: uuid,
  name: z.string(),
  competitionId: uuid,
  competitionName: z.string(),
  competitionStatus: z.string(),
  typeCode: z.enum(COMMISSIONING_CONFIGURATOR_TYPE_CODES),
  typeName: z.string(),
  templateId: uuid,
  templateName: z.string(),
  templateActive: z.boolean(),
  scope: z.enum(COMMISSIONING_INSTANCE_SCOPES),
  isActive: z.boolean(),
  /** Famiglia evento dichiarata (null = eredita dal VP/famiglia gara). */
  eventFamily: z.enum(COMMISSIONING_EVENT_SOURCE_TYPES).nullable(),
  periodStart: z.string(),
  periodEnd: z.string(),
  /** 'instance' = periodo proprio; 'competition' = eredita dalla gara. */
  periodSource: z.enum(['instance', 'competition']),
  rulesVersion: z.number().int().nonnegative(),
}).strict();
export type CommissioningSchedaIdentity = z.infer<typeof commissioningSchedaIdentitySchema>;

export const commissioningSchedaDriversSchema = z.object({
  mode: z.enum(COMMISSIONING_SCHEDA_DRIVER_MODES),
  /** Driver di gara dell'istanza (null = extra-driver). */
  businessDriver: schedaDriverRefSchema.nullable(),
  /** Driver sorgente (solo extra-driver con sorgenti). */
  sourceDrivers: z.array(schedaDriverRefSchema),
  /** Configuratori da cui questa istanza legge dati via variabili cross_ref. */
  crossRefDependencies: z.array(z.object({ id: uuid, name: z.string() }).strict()),
}).strict();
export type CommissioningSchedaDrivers = z.infer<typeof commissioningSchedaDriversSchema>;

export const commissioningSchedaValuePackageSchema = z.object({
  /** Regola condivisa BE/FE: il tipo/la dichiarazione richiedono un VP della gara. */
  required: z.boolean(),
  /** Motivo leggibile della richiesta (null se non richiesto). */
  requiredReason: z.string().nullable(),
  /** VP scelto sul configuratore (ereditato da cluster e celle); null = nessuno. */
  value: z.object({ id: uuid, name: z.string() }).strict().nullable(),
  /** Numero di VP abbinati alla gara (0 = prima va abbinato un VP alla gara). */
  competitionPackageCount: z.number().int().nonnegative(),
}).strict();
export type CommissioningSchedaValuePackage = z.infer<typeof commissioningSchedaValuePackageSchema>;

export const commissioningSchedaClusterDriverSchema = z.object({
  businessDriverId: uuid,
  driverName: z.string(),
  tgtStandard: z.number().nullable(),
  tgtUnit: z.string().nullable(),
  tgtOverrideCount: z.number().int().nonnegative(),
  /** Membri attivi del cluster senza TGT (né standard né personale). */
  membersWithoutTgt: z.number().int().nonnegative(),
}).strict();
export type CommissioningSchedaClusterDriver = z.infer<typeof commissioningSchedaClusterDriverSchema>;

export const commissioningSchedaClusterSchema = z.object({
  assignmentId: uuid,
  competitionClusterId: uuid,
  name: z.string(),
  entityType: z.enum(COMMISSIONING_CLUSTER_ENTITY_TYPES),
  /** Asse del cluster (null = Overall). */
  clusterBusinessDriverId: uuid.nullable(),
  memberCount: z.number().int().nonnegative(),
  isActive: z.boolean(),
  hasRuleVariant: z.boolean(),
  thresholdValuesStatus: commissioningThresholdValuesStatusSchema,
  drivers: z.array(commissioningSchedaClusterDriverSchema),
}).strict();
export type CommissioningSchedaCluster = z.infer<typeof commissioningSchedaClusterSchema>;

export const commissioningSchedaFunctionSchema = z.object({
  assignmentId: uuid,
  functionId: uuid,
  functionName: z.string(),
  functionStatus: z.enum(COMMISSIONING_PUBLICATION_STATUSES),
  sequence: z.number().int(),
  matchLevers: z.array(commissioningFunctionEffectSchema),
  elseLevers: z.array(commissioningFunctionEffectSchema),
}).strict();
export type CommissioningSchedaFunction = z.infer<typeof commissioningSchedaFunctionSchema>;

/** Paletto del template così come il motore lo valuta (config.gates, AND logico). */
export const commissioningSchedaGateSchema = z.object({
  index: z.number().int().nonnegative(),
  gate: z.custom<CommissioningGateV2>((value) => typeof value === 'object' && value !== null && 'kind' in value),
  /** Nome della funzione L3 (solo kind function_l3; null = non trovata). */
  functionName: z.string().nullable(),
}).strict();
export type CommissioningSchedaGate = z.infer<typeof commissioningSchedaGateSchema>;

export const commissioningSchedaCapSchema = z.object({
  capType: z.enum(COMMISSIONING_CAP_TYPES).nullable(),
  capValue: z.number().nullable(),
  capMetric: z.enum(COMMISSIONING_CAP_METRICS).nullable(),
  capBaseVariableCode: z.string().nullable(),
  capScopeFilterFunctionId: uuid.nullable(),
  capScopeFilterFunctionName: z.string().nullable(),
  /** true = il tetto è impostato sull'istanza (cap_type + cap_value). */
  configured: z.boolean(),
  /** true = il template dichiara dei default CAP (informativo). */
  templateHasDefaults: z.boolean(),
}).strict();
export type CommissioningSchedaCap = z.infer<typeof commissioningSchedaCapSchema>;

/** Regole di periodo consumate dagli aggregati (istanza + template). */
export const commissioningSchedaPeriodRulesSchema = z.object({
  hasThresholds: z.boolean(),
  hasRanking: z.boolean(),
  checkpointCount: z.number().int().nonnegative(),
  /** Regole bonus/malus per gate (aggregate_rules.bonusMalus) con nome funzione. */
  bonusMalus: z.array(z.object({
    functionId: uuid,
    functionName: z.string().nullable(),
  }).strict()),
  /** Bonus/malus sul totale di periodo post-CAP dal template (null = assente). */
  totalBonusMalus: z.custom<CommissioningTotalBonusMalusConfig>(
    (value) => typeof value === 'object' && value !== null && 'bonus' in value && 'malus' in value,
  ).nullable(),
  /** Tipo bonus_malus: regole KPI con banda morta (template). */
  bonusMalusRuleCount: z.number().int().nonnegative(),
  gettoneRuleCount: z.number().int().nonnegative(),
  /** Tipo classifica: fasce di posizione del template (i premi sono nei valori per cluster). */
  rankingPrizeCount: z.number().int().nonnegative(),
  /** Tipo aggregazione: sorgenti e livelli del template (pesi/punteggi/premi nei valori per cluster). */
  aggregationSourceCount: z.number().int().nonnegative(),
  aggregationLevelCount: z.number().int().nonnegative(),
}).strict();
export type CommissioningSchedaPeriodRules = z.infer<typeof commissioningSchedaPeriodRulesSchema>;

/**
 * Risposta GET `/configurator-instances/:id/scheda`: vista unica, in sola
 * lettura, dei legami di un configuratore in gara. Compone letture già
 * esposte (istanza, assegnazioni, funzioni, template, preflight): nessuna
 * fonte di verità nuova.
 */
export const commissioningInstanceSchedaSchema = z.object({
  identity: commissioningSchedaIdentitySchema,
  drivers: commissioningSchedaDriversSchema,
  valuePackage: commissioningSchedaValuePackageSchema,
  /** Perimetro del configuratore con nomi risolti (dimensioni dichiarate, binario, stato dei riferimenti). */
  perimeter: commissioningPerimeterSchedaSchema,
  clusters: z.array(commissioningSchedaClusterSchema),
  functions: z.array(commissioningSchedaFunctionSchema),
  gates: z.array(commissioningSchedaGateSchema),
  cap: commissioningSchedaCapSchema,
  periodRules: commissioningSchedaPeriodRulesSchema,
  checklist: commissioningSchedaChecklistSchema,
}).strict();
export type CommissioningInstanceScheda = z.infer<typeof commissioningInstanceSchedaSchema>;
